// src/components/common/CookieConsent/CookieConsentBanner.jsx
//
// БАННЕР СОГЛАСИЯ НА КУКИ.
//
// 📌 ОБРАЗЕЦ ВЗЯТ ИЗ NORRAN (`frontend/src/components/CookieConsentBanner.jsx`).
//    Владелец (2026-10-07): «Токены посмотри как мы сделали в norran, так и
//    сделаем» — тот же подход применён и к согласиям, потому что в NORRAN
//    это уже продумано и разобрано.
//
// ─────────────────────────────────────────────────────────────────────────
// 🔴 ГЛАВНОЕ РЕШЕНИЕ: «СПРОСИТЬ» ≠ «ПОКАЗАТЬ БАННЕР»
//
//    Соблазн: показывать баннер всегда, пока в localStorage нет отметки.
//    ЭТО ДЕФЕКТ. Отметка в localStorage исчезает при очистке хранилища, при
//    входе с другого устройства и в приватном окне — то есть «просить
//    всегда» превратилось бы в «просить при каждом удобном случае».
//    Хуже другое: ЗАКОННОСТЬ обработки держалась бы на записи в БРАУЗЕРЕ,
//    а не на сервере. По ч. 1 ст. 9 152-ФЗ оператор ОБЯЗАН ДОКАЗАТЬ наличие
//    согласия — галочка в браузере не доказывает ничего.
//
//    ✅ ПОЭТОМУ ИСТОЧНИК ПРАВДЫ — СЕРВЕР. При загрузке спрашиваем
//    `GET /api/v1/consent/status`; баннер показывается ТОЛЬКО если сервер
//    ответил «согласия на куки нет». Аноним опознаётся по `anonymous_id`,
//    который лежит в localStorage как ПОДСКАЗКА серверу (кто именно
//    спрашивает), а не как доказательство согласия.
//
// ⚠️ ПОЧЕМУ КНОПКА ОТКАЗА ЕСТЬ, А СКРЫТИЯ НЕДОСТАТОЧНО
//    Согласие по 152-ФЗ — ДЕЙСТВИЕ субъекта, а не молчание. Поэтому
//    «Принять» фиксирует согласие на сервере, а «Только необходимые»
//    закрывает баннер БЕЗ записи согласия: необходимые cookie работают на
//    законном интересе. Записать отказ как согласие было бы подлогом.
//
// ⚠️ СОСТОЯНИЕ РАБОТЫ (важно, не считай готовым):
//    - Фронтенд готов и самодостаточен.
//    - Бэкенд-часть (`/api/v1/consent/*`, таблица `consent_records`)
//      В СТУДИИ ЕЩЁ НЕ СДЕЛАНА — см. `ARCHITECTURE_TASKS.txt` п.11.
//      Пока её нет, `check()` получит ошибку и баннер НЕ покажется
//      (см. разбор в catch) — то есть поведение безопасное, но согласия
//      не фиксируются.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import client from '../../../api/client.js';
import { getAnonymousId } from '../../../utils/anonymousId.js';

// ⚠️ `getAnonymousId` ВЫНЕСЕН В ОБЩИЙ МОДУЛЬ (`utils/anonymousId.js`).
//    Он понадобился ещё и при регистрации (фиксация согласия на ПДн), а две
//    КОПИИ с одним ключом разошлись бы при первой же правке: одна поменяла бы
//    ключ, другая нет — и сервер увидел бы ДВУХ разных анонимов у одного
//    браузера. Ключ объявлен ОДИН раз.

export const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // 🔑 ПРОВЕРКА НА СЕРВЕРЕ, А НЕ В БРАУЗЕРЕ — см. разбор в шапке файла.
  const check = useCallback(async () => {
    try {
      const anonymousId = getAnonymousId();
      const res = await client.get('/consent/status', {
        params: anonymousId ? { anonymous_id: anonymousId } : {},
      });
      const consents = res.data?.consents || {};
      // Баннер нужен, пока нет согласия именно на куки.
      setVisible(consents.cookie !== true);
    } catch {
      // ⚠️ Сервер не ответил (или эндпоинта ещё нет) — баннер НЕ показываем.
      //    Показать его «на всякий случай» значило бы спрашивать согласие,
      //    которое нельзя записать: нажатие всё равно упало бы. А молчание
      //    безопаснее: обработка необязательных cookie и так не начинается
      //    без согласия.
      setVisible(false);
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  /** Фиксирует согласие на сервере. Тип — `cookie`. */
  const accept = async () => {
    setSaving(true);
    setError('');
    try {
      const anonymousId = getAnonymousId();
      await client.post('/consent', {
        consent_type: 'cookie',
        document_version: '1.0',
        anonymous_id: anonymousId,
      });
      setVisible(false);
    } catch {
      setError('Не удалось сохранить выбор. Попробуйте ещё раз.');
    } finally {
      setSaving(false);
    }
  };

  /**
   * «Только необходимые» — закрывает баннер БЕЗ записи согласия.
   *
   * ⚠️ Это НЕ отказ, зафиксированный на сервере, и это осознанно:
   *    необходимые cookie работают на законном интересе и не требуют
   *    согласия. Записать отказ как согласие было бы подлогом.
   */
  const declineOptional = () => {
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Согласие на использование файлов cookie"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        backgroundColor: 'rgb(var(--c-surface))',
        borderTop: '1px solid rgb(var(--c-border))',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.08)',
        padding: '20px 24px',
      }}
    >
      <div
        className="container"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '16px',
          justifyContent: 'space-between',
        }}
      >
        <p
          style={{
            fontSize: '14px',
            color: 'rgb(var(--c-text-secondary))',
            lineHeight: 1.6,
            margin: 0,
            flex: '1 1 420px',
          }}
        >
          Мы используем файлы cookie. Необходимые обеспечивают работу сайта,
          необязательные — помогают его улучшать. Подробнее — в{' '}
          <Link to="/privacy" style={{ color: 'rgb(var(--c-accent))' }}>
            Политике обработки персональных данных
          </Link>
          .
        </p>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={declineOptional}
            disabled={saving}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 500,
              color: 'rgb(var(--c-text-secondary))',
              backgroundColor: 'rgb(var(--c-surface-2))',
              border: '1px solid #E0E0E0',
              borderRadius: '8px',
              cursor: saving ? 'default' : 'pointer',
            }}
          >
            Только необходимые
          </button>
          <button
            type="button"
            onClick={accept}
            disabled={saving}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 500,
              color: 'rgb(var(--c-on-accent))',
              backgroundColor: 'rgb(var(--c-accent))',
              border: 'none',
              borderRadius: '8px',
              cursor: saving ? 'default' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Сохраняем…' : 'Принять'}
          </button>
        </div>
      </div>

      {error && (
        <p
          style={{
            fontSize: '13px',
            color: '#C62828',
            margin: '10px 0 0',
            textAlign: 'right',
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
};
